/**
 * Monitoring Integration Utilities
 * Provides comprehensive integration of error handling, logging, and monitoring
 */

import { logger } from '../services/LoggingService';
import { errorHandler, ErrorType, ErrorSeverity } from './errorHandler';
import React from 'react';

// Type definition for browser Memory API
interface MemoryInfo {
  usedJSHeapSize: number;
  totalJSHeapSize: number;
  jsHeapSizeLimit: number;
}

/**
 * Initialize monitoring system
 * Call this once at application startup
 */
export function initializeMonitoring() {
  logger.info('Initializing monitoring system', 'MonitoringIntegration');

  // Set up error handler callbacks
  errorHandler.onError((error) => {
    // Log errors with structured logging in development
    if (process.env.NODE_ENV === 'development') {
      logger.error(`Error occurred: ${error.type}`, 'MonitoringIntegration', {
        severity: error.severity,
        context: error.context,
        userMessage: error.userMessage
      }, error);
    }

    // Show user-friendly toast notifications for certain errors
    if (error.severity === ErrorSeverity.HIGH || error.severity === ErrorSeverity.CRITICAL) {
      // This would integrate with your toast system
      // showToast({ type: 'error', message: error.userMessage });
      logger.warn('High severity error occurred', 'MonitoringIntegration', {
        userMessage: error.userMessage,
        severity: error.severity
      });
    }
  });

  // Set up performance monitoring
  if ('performance' in window) {
    // Monitor long tasks
    const observer = new PerformanceObserver((list) => {
      list.getEntries().forEach((entry) => {
        if (entry.duration > 50) { // Tasks longer than 50ms
          logger.warn('Long task detected', 'MonitoringIntegration', {
            duration: entry.duration,
            startTime: entry.startTime,
            name: entry.name
          });
        }
      });
    });

    try {
      observer.observe({ entryTypes: ['longtask'] });
    } catch (e) {
      // longtask may not be supported in all browsers
      logger.debug('Longtask monitoring not supported', 'MonitoringIntegration');
    }

    // Monitor navigation timing
    window.addEventListener('load', () => {
      setTimeout(() => {
        const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
        if (navigation) {
          logger.info('Page load performance', 'MonitoringIntegration', {
            domContentLoaded: navigation.domContentLoadedEventEnd - navigation.domContentLoadedEventStart,
            loadComplete: navigation.loadEventEnd - navigation.loadEventStart,
            totalTime: navigation.loadEventEnd - navigation.fetchStart
          });
        }
      }, 0);
    });
  }

  // Monitor unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    const error = new Error(`Unhandled promise rejection: ${event.reason}`);
    errorHandler.handleError(error, {
      component: 'Promise',
      operation: 'Unhandled rejection',
      metadata: { reason: event.reason }
    });
  });

  // Set up memory monitoring (if supported)
  if ('memory' in performance) {
    setInterval(() => {
      const memory = (performance as Performance & { memory?: MemoryInfo }).memory;
      if (memory) {
        const memoryUsage = {
          used: Math.round(memory.usedJSHeapSize / 1048576), // MB
          total: Math.round(memory.totalJSHeapSize / 1048576), // MB
          limit: Math.round(memory.jsHeapSizeLimit / 1048576) // MB
        };

        // Log if memory usage is high
        if (memoryUsage.used / memoryUsage.limit > 0.8) {
          logger.warn('High memory usage detected', 'MonitoringIntegration', memoryUsage);
        }
      }
    }, 30000); // Check every 30 seconds
  }

  logger.info('Monitoring system initialized', 'MonitoringIntegration');
}

/**
 * Decorator for monitoring async operations
 */
export function monitorAsync<T extends (...args: unknown[]) => Promise<unknown>>(
  operation: string,
  func: T,
  options: {
    timeout?: number;
    context?: string;
    errorType?: ErrorType;
  } = {}
): T {
  return ((...args: Parameters<T>) => {
    const startTime = performance.now();
    const { timeout = 30000, context = 'Unknown' } = options;

    logger.debug(`Starting async operation: ${operation}`, context || 'MonitoringIntegration', { args });

    // Create timeout promise
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => {
        reject(new Error(`Operation ${operation} timed out after ${timeout}ms`));
      }, timeout);
    });

    // Race between the actual operation and timeout
    return Promise.race([
      func(...args),
      timeoutPromise
    ]).then(
      (result) => {
        const duration = performance.now() - startTime;
        logger.info(`Async operation completed: ${operation}`, context || 'MonitoringIntegration', {
          duration: Math.round(duration),
          success: true
        });
        return result;
      },
      (error) => {
        const duration = performance.now() - startTime;
        logger.error(`Async operation failed: ${operation}`, context || 'MonitoringIntegration', {
          duration: Math.round(duration),
          success: false,
          error: error.message
        });

        // Create structured error
        const monitoredError = new Error(`${operation} failed: ${error.message}`);
        errorHandler.handleError(monitoredError, {
          component: context,
          operation,
          metadata: { originalError: error, duration }
        });

        throw error;
      }
    ) as ReturnType<T>;
  }) as unknown as T;
}

/**
 * Monitor component mount/unmount cycles
 */
export function monitorComponent(componentName: string) {
  return {
    onMount: () => {
      logger.debug(`Component mounted: ${componentName}`);
    },
    onUnmount: () => {
      logger.debug(`Component unmounted: ${componentName}`);
    },
    onError: (error: Error, errorInfo: React.ErrorInfo) => {
      errorHandler.handleError(error, {
        component: componentName,
        operation: 'React lifecycle',
        metadata: { componentStack: errorInfo.componentStack }
      });
    }
  };
}

/**
 * Monitor API calls
 */
export async function monitoredFetch(
  url: string,
  options: RequestInit = {},
  context?: string
): Promise<Response> {
  const startTime = performance.now();
  const operation = `API ${options.method || 'GET'} ${url}`;

  logger.debug(`Starting API call: ${operation}`, context || 'MonitoringIntegration', { options });

  try {
    const response = await fetch(url, options);
    const duration = performance.now() - startTime;

    if (!response.ok) {
      logger.error(`API call failed: ${operation}`, context || 'MonitoringIntegration', {
        status: response.status,
        statusText: response.statusText,
        duration: Math.round(duration)
      });

      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    logger.info(`API call completed: ${operation}`, context || 'MonitoringIntegration', {
      status: response.status,
      duration: Math.round(duration),
      success: true
    });

    return response;
  } catch (error) {
    const duration = performance.now() - startTime;
    
    logger.error(`API call error: ${operation}`, context || 'MonitoringIntegration', {
      duration: Math.round(duration),
      error: error instanceof Error ? error.message : error
    });

    // Determine error type based on error characteristics
    // let errorType = ErrorType.NETWORK;
    // if (error instanceof Error) {
    //   if (error.message.includes('401')) errorType = ErrorType.AUTHENTICATION;
    //   else if (error.message.includes('403')) errorType = ErrorType.AUTHORIZATION;
    //   else if (error.message.includes('400')) errorType = ErrorType.VALIDATION;
    //   else if (error.message.includes('500')) errorType = ErrorType.SYSTEM;
    // }

    errorHandler.handleError(error instanceof Error ? error : new Error(String(error)), {
      component: context,
      operation,
      metadata: { url, options, duration }
    });

    throw error;
  }
}

/**
 * Monitor user interactions
 */
export function monitorUserAction(
  action: string,
  element?: string,
  metadata?: Record<string, unknown>
) {
  logger.info(`User action: ${action}`, 'UserAction', {
    element,
    timestamp: new Date().toISOString(),
    ...metadata
  });
}

/**
 * Create monitored event handlers
 */
export function createMonitoredHandler<T extends Event>(
  handler: (event: T) => void | Promise<void>,
  actionName: string,
  element?: string
) {
  return async (event: T) => {
    monitorUserAction(actionName, element, {
      eventType: event.type,
      target: (event.target as Element)?.tagName
    });

    try {
      await handler(event);
    } catch (error) {
      errorHandler.handleError(error instanceof Error ? error : new Error(String(error)), {
        component: element,
        operation: actionName,
        metadata: { eventType: event.type }
      });
      throw error;
    }
  };
}

/**
 * Get monitoring metrics for dashboard
 */
export function getMonitoringMetrics() {
  const logs = localStorage.getItem('app_logs');
  const parsedLogs = logs ? JSON.parse(logs) : [];

  const now = Date.now();
  const oneHourAgo = now - (60 * 60 * 1000);
  const oneDayAgo = now - (24 * 60 * 60 * 1000);

  interface LogEntry {
    timestamp: string;
    level: string;
  }

  const recentLogs = parsedLogs.filter((log: LogEntry) => 
    new Date(log.timestamp).getTime() > oneHourAgo
  );

  const dailyLogs = parsedLogs.filter((log: LogEntry) => 
    new Date(log.timestamp).getTime() > oneDayAgo
  );

  return {
    totalLogs: parsedLogs.length,
    recentLogs: recentLogs.length,
    dailyLogs: dailyLogs.length,
    errorRate: {
      hourly: recentLogs.filter((log: LogEntry) => log.level === 'ERROR').length / Math.max(recentLogs.length, 1),
      daily: dailyLogs.filter((log: LogEntry) => log.level === 'ERROR').length / Math.max(dailyLogs.length, 1)
    },
    logsByLevel: {
      error: parsedLogs.filter((log: LogEntry) => log.level === 'ERROR').length,
      warn: parsedLogs.filter((log: LogEntry) => log.level === 'WARN').length,
      info: parsedLogs.filter((log: LogEntry) => log.level === 'INFO').length,
      debug: parsedLogs.filter((log: LogEntry) => log.level === 'DEBUG').length
    },
    memoryUsage: (performance as Performance & { memory?: MemoryInfo }).memory ? {
      used: Math.round((performance as Performance & { memory?: MemoryInfo }).memory!.usedJSHeapSize / 1048576),
      total: Math.round((performance as Performance & { memory?: MemoryInfo }).memory!.totalJSHeapSize / 1048576),
      limit: Math.round((performance as Performance & { memory?: MemoryInfo }).memory!.jsHeapSizeLimit / 1048576)
    } : null
  };
}

/**
 * Export monitoring data
 */
export function exportMonitoringData() {
  const logs = localStorage.getItem('app_logs');
  const metrics = getMonitoringMetrics();
  
  const exportData = {
    timestamp: new Date().toISOString(),
    metrics,
    logs: logs ? JSON.parse(logs) : [],
    userAgent: navigator.userAgent,
    url: window.location.href
  };

  const blob = new Blob([JSON.stringify(exportData, null, 2)], { 
    type: 'application/json' 
  });
  
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `monitoring-data-${Date.now()}.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);

  logger.info('Monitoring data exported', 'MonitoringIntegration');
}